// WHO THIS JOB'S INVOICE NAMES — decided here, on the job card.
//
// Jacky's rule: "any job connect to a customer that belong to account, we invoice
// account, not the person", with the exception "unless switch flipped for personal
// job". His case is a real-estate agent who owns their own house — the agency
// cannot be billed, so the person is.
//
// The party is NOT worked out here. It comes from one server function
// (`resolveBillingParty`), because if this card and the downstream consumer each
// decided for themselves they would eventually disagree about the same job, and
// nobody would notice until month end. This control shows that answer and offers
// the exception; it never computes one.
//
// The switch belongs to the JOB, not the contact: one agent can have a business
// job and a private job, and the two bill differently.
import {useState} from "react";
import {useQuery,useQueryClient} from "@tanstack/react-query";
import {Button,Textarea} from "@aiw/ui";
import {api,client} from "../lib/api";

export function BillToControl({jobRef}:{jobRef:string}){
  const [open,setOpen]=useState(false);
  const [reason,setReason]=useState("");
  const [acctOpen,setAcctOpen]=useState(false);
  const [acctId,setAcctId]=useState("");
  const [acctReason,setAcctReason]=useState("");
  const [busy,setBusy]=useState(false);
  const [notice,setNotice]=useState("");
  const qc=useQueryClient();

  const party=useQuery({
    queryKey:["billing-party",jobRef],
    enabled:!!jobRef,
    queryFn:()=>api.jobBillingParty(client,jobRef),
  });

  // Only fetched when the account picker is opened — most job cards never need it.
  const accounts=useQuery({
    queryKey:["accounts","for-billing"],
    enabled:acctOpen,
    queryFn:()=>api.accounts(client,{limit:"200"}),
  });

  // Correcting WHICH account the job bills. Separate from the personal switch on
  // purpose: that one says "bill the person instead", this one says "bill a
  // different business". Both are per-job and both need a reason.
  async function pickAccount(id:string){
    if(busy||!id)return;
    setBusy(true);setNotice("");
    try{
      const r=await api.propose(client,{
        command:"set_job_account",
        idempotency_key:crypto.randomUUID(),
        args:{job_ref:jobRef,account_id:id,reason:acctReason.trim()},
      });
      setNotice(
        r.status==="executed"
          ? "This job now bills that account. The account itself and this customer's other jobs were not touched."
          : r.status==="pending"
            ? "The change awaits approval. Nothing has moved yet."
            : `Not changed: ${r.reason}`,
      );
      if(r.status==="executed"||r.status==="pending"){setAcctOpen(false);setAcctReason("");setAcctId("");}
      await qc.invalidateQueries();
    }catch{
      setNotice("The change could not be verified. Refresh and check who this job bills before trying again.");
    }finally{setBusy(false);}
  }

  async function flip(personal:boolean){
    if(busy)return;
    setBusy(true);setNotice("");
    try{
      const r=await api.propose(client,{
        command:"set_job_billing",
        idempotency_key:crypto.randomUUID(),
        args:{job_ref:jobRef,personal,reason:personal?reason.trim():null},
      });
      setNotice(
        r.status==="executed"
          ? (personal?"This job now bills the person. No other job and no account was touched.":"This job bills its account again.")
          : r.status==="pending"
            ? "The change awaits approval. Nothing has moved yet."
            : `Not changed: ${r.reason}`,
      );
      if(r.status==="executed"||r.status==="pending"){setOpen(false);setReason("");}
      await qc.invalidateQueries();
    }catch{
      setNotice("The change could not be verified. Refresh and check who this job bills before trying again.");
    }finally{setBusy(false);}
  }

  if(party.isPending)return <p className="text-sm text-muted-foreground">Working out who this job bills…</p>;
  const p=party.data;
  if(!p)return <p className="text-sm text-muted-foreground">Billing party could not be read. Refresh before relying on it.</p>;

  // No contact and no account: there is nobody to bill. Say so rather than
  // offering a switch that names nobody.
  const nobody=!p.account_id&&!p.via_contact_id;

  return <div className="grid gap-2 text-sm">
    <p>
      {p.kind==="account"
        ? <>Invoices <strong>{p.name}</strong>{p.via_contact_name?<> · via {p.via_contact_name}</>:null}</>
        : <>Invoices <strong>{p.name}</strong>{p.personal?<> · the person, not the business</>:null}</>}
    </p>
    {p.personal&&p.reason&&(
      <p className="text-micro text-muted-foreground">Reason recorded: {p.reason}</p>
    )}
    {nobody&&(
      <p className="text-micro text-muted-foreground">
        This job has no customer attached, so there is nobody to bill yet.
      </p>
    )}

    {!nobody&&!open&&(p.personal
      ? <Button size="sm" variant="outline" disabled={busy} onClick={()=>void flip(false)}>Bill the account again</Button>
      : p.account_id
        ? <Button size="sm" variant="outline" disabled={busy} onClick={()=>setOpen(true)}>Personal job — bill the person…</Button>
        : null)}

    {open&&(
      <div className="grid gap-2">
        <label htmlFor={`bill-reason-${jobRef}`}>
          Why does this job bill the person? The agency cannot be billed, the owner is paying…
        </label>
        <Textarea
          id={`bill-reason-${jobRef}`}
          value={reason}
          onChange={(e)=>setReason(e.target.value)}
          maxLength={200}
          placeholder="Owner&rsquo;s own home — the agency cannot be billed"
        />
        <p className="text-micro text-muted-foreground">
          This decides who the job is billed to. Only this job changes — the customer&rsquo;s other jobs and the account are untouched.
        </p>
        <div className="flex gap-2">
          <Button size="sm" disabled={busy||reason.trim().length<3} onClick={()=>void flip(true)}>
            Bill this person
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={()=>{setOpen(false);setReason("");}}>Back</Button>
        </div>
      </div>
    )}

    {!nobody&&!acctOpen&&!open&&(
      <Button size="sm" variant="outline" disabled={busy} onClick={()=>setAcctOpen(true)}>
        {p.personal?"Bill an account instead…":"Change the billing account…"}
      </Button>
    )}

    {acctOpen&&(
      <div className="grid gap-2">
        <label htmlFor={`bill-acct-${jobRef}`}>Which account should this job bill?</label>
        <select
          id={`bill-acct-${jobRef}`}
          className="rounded border bg-background p-1"
          value={acctId}
          onChange={(e)=>setAcctId(e.target.value)}
        >
          <option value="">— choose an account —</option>
          {(accounts.data??[]).map((a)=>(
            <option key={String(a.id)} value={String(a.id)}>{String(a.name)}</option>
          ))}
        </select>
        <label htmlFor={`bill-acct-why-${jobRef}`}>Why is the account changing?</label>
        <Textarea
          id={`bill-acct-why-${jobRef}`}
          value={acctReason}
          onChange={(e)=>setAcctReason(e.target.value)}
          maxLength={200}
          placeholder="The builder is paying, not the owner"
        />
        <p className="text-micro text-muted-foreground">
          The job keeps the account it is billed under, so if this customer later changes employer this job does not move with them.
        </p>
        <div className="flex gap-2">
          <Button size="sm" disabled={busy||!acctId||acctReason.trim().length<3} onClick={()=>void pickAccount(acctId)}>
            Bill this account
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={()=>{setAcctOpen(false);setAcctReason("");setAcctId("");}}>Back</Button>
        </div>
      </div>
    )}

    {notice&&<p role="status" className="text-sm">{notice}</p>}
  </div>;
}
